// HTTP wrappers for the four GrataQ endpoints the SPA touches.
//
// Each non-2xx response is mapped to a typed Error. Callers catch by class
// name (instanceof) and translate to a UI state. We do this here, not at
// the call site, so the rest of the app deals in domain errors instead of
// HTTP status codes.

export class LinkNotFoundError extends Error {}
export class LinkExpiredError extends Error {}
export class LinkPausedError extends Error {}
export class NoPrekeysError extends Error {}
export class AllotmentExhaustedError extends Error {}
export class TooLargeError extends Error {}
export class UploadFailedError extends Error {}

/**
 * API base URL. Resolution order:
 *   1. Explicit window.GRATAQ_API_BASE (Playwright tests, custom embeds).
 *   2. Auto-detected localhost dev mode -> http://localhost:5243.
 *   3. Production default -> https://api.grataq.com.
 */
export function apiBase() {
  if (typeof window === 'undefined') return 'https://api.grataq.com';
  // eslint-disable-next-line no-undef
  if (window.GRATAQ_API_BASE) return window.GRATAQ_API_BASE;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return 'http://localhost:5243';
  }
  return 'https://api.grataq.com';
}

/** GET /api/v1/upload-links/{token}/config — public. */
export async function getConfig(token) {
  const resp = await fetch(`${apiBase()}/api/v1/upload-links/${encodeURIComponent(token)}/config`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (resp.status === 404) throw new LinkNotFoundError();
  if (resp.status === 410) throw new LinkExpiredError();
  if (!resp.ok) throw new Error(`config fetch failed: HTTP ${resp.status}`);
  return await resp.json();
}

/** GET /api/v1/accounts/{handle}/prekey — atomically consumes one pre-key. */
export async function getPrekey(handle) {
  const resp = await fetch(`${apiBase()}/api/v1/accounts/${encodeURIComponent(handle)}/prekey`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (resp.status === 404) throw new NoPrekeysError();
  // 429 is overloaded on this endpoint: the Model B allotment block returns
  // { error: "allotment_exhausted" }, while the rate-limiter returns 429 with a
  // different (or empty) body. Only the former means "recipient is full"; the
  // latter is transient, so let it fall through to the generic retry message.
  if (resp.status === 429) {
    const code = await resp.json().then((b) => b && b.error).catch(() => null);
    if (code === 'allotment_exhausted') throw new AllotmentExhaustedError();
    throw new Error('prekey fetch rate-limited: HTTP 429');
  }
  if (!resp.ok) throw new Error(`prekey fetch failed: HTTP ${resp.status}`);
  return await resp.json();
}

/**
 * POST /api/v1/accounts/{handle}/blobs?upload_token={token} — multipart.
 * Uses XMLHttpRequest to expose upload progress (fetch can't yet).
 *
 * @param handle      recipient handle from config
 * @param token       upload-link token
 * @param formData    a FormData with prekey_id, kem_ciphertext, iv, encrypted_file
 * @param onProgress  (loaded, total) -> void; called frequently during upload
 * @returns           { blob_id, expires_at } from the 201 response
 */
export function uploadBlob(handle, token, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const url = `${apiBase()}/api/v1/accounts/${encodeURIComponent(handle)}/blobs`
      + `?upload_token=${encodeURIComponent(token)}`;

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.responseType = 'json';

    xhr.upload.onprogress = (ev) => {
      if (onProgress && ev.lengthComputable) {
        onProgress(ev.loaded, ev.total);
      }
    };

    xhr.onerror = () => reject(new UploadFailedError('network error'));
    xhr.onabort = () => reject(new UploadFailedError('aborted'));
    xhr.ontimeout = () => reject(new UploadFailedError('timeout'));
    xhr.onload = () => {
      const status = xhr.status;
      if (status === 201) {
        resolve(xhr.response);
        return;
      }
      if (status === 410) reject(new LinkExpiredError());
      else if (status === 409) reject(new LinkPausedError());
      else if (status === 413) reject(new TooLargeError());
      else if (status === 404) reject(new LinkNotFoundError());
      else reject(new UploadFailedError(`HTTP ${status}`));
    };

    xhr.send(formData);
  });
}
